<template>
  <div>
      <div v-if="openWeather.description" class="weather-modal__description" v-html="openWeather.description"></div>
      <div v-if="openWeather.temperature" class="weather-modal__wrapper">
        <div class="weather-modal__current-weather current-weather">
          <div class="current-weather__icon-row">
            <div class="current-weather__temperature">
            {{openWeather && openWeather.temperature !== null
              ? openWeather.temperature + 'ºC'
              : noData}}
            </div>
          </div>
        </div>
        <div class="weather-modal__weather-details">
          <div class="weather-modal__icon-data">
            <div class="weather-modal__icon-data-icon">
              <i class="icomoon-meteo-humidity"></i>
            </div>
            <div class="weather-modal__icon-data-value">
            {{openWeather && openWeather.humidity !== null
              ? openWeather.humidity + '% RH'
              : noData}}
            </div>
          </div>
          <div class="weather-modal__icon-data">
            <div class="weather-modal__icon-data-icon">
              <i class="icomoon-meteo-pressure"></i>
            </div>
            <div class="weather-modal__icon-data-value">
            {{openWeather && openWeather.pressure !== null
              ? openWeather.pressure + ' hPa'
              : noData}}
            </div>
          </div>
          <div class="weather-modal__icon-data">
            <div class="weather-modal__icon-data-icon">
              <i class="icomoon-meteo-heavy-wind"></i>
            </div>
            <div class="weather-modal__icon-data-value">
            {{openWeather && openWeather.windSpeed !== null
              ? openWeather.windSpeed + ' m/s'
              : noData}}
            </div>
          </div>
          <div class="weather-modal__icon-data">
            <div class="weather-modal__icon-data-icon">
              <i :class="openWeather && openWeather.windDegrees !== null
                          ? directionToIconMapper(direction(openWeather.windDegrees)) :
                          noData">
              </i>
            </div>
            <div class="weather-modal__icon-data-value">
            {{openWeather && openWeather.windDegrees !== null
              ? direction(openWeather.windDegrees)
              : '-'}}
            </div>
          </div>
        </div>
        <div class="weather-modal__air-quality">
          <div 
            class="weather-modal__text-data" 
            :title="paramsTitles.airCO[currentLanguageNumber]"
            :style="{ 'background-color': AirQualityService.getColor('CO', openWeather.co) }"
          >
            <div class="weather-modal__text-data-label">CO</div>
            <div class="weather-modal__text-data-value">
              {{openWeather && openWeather.co !== null ? openWeather.co + ' µg/m³' : noData}}
            </div>
          </div>
          <div 
            class="weather-modal__text-data" 
            :title="paramsTitles.airNO2[currentLanguageNumber]"
            :style="{ 'background-color': AirQualityService.getColor('NO2', openWeather.no2) }"
          >
            <div class="weather-modal__text-data-label">NO2</div>
            <div class="weather-modal__text-data-value">
              {{openWeather && openWeather.no2 !== null ? openWeather.no2 + ' µg/m³' : noData}}
            </div>
          </div>
          <div 
            class="weather-modal__text-data" 
            :title="paramsTitles.airSO2[currentLanguageNumber]"
            :style="{ 'background-color': AirQualityService.getColor('SO2', openWeather.so2) }"
          >
            <div class="weather-modal__text-data-label">SO2</div>
            <div class="weather-modal__text-data-value">
              {{openWeather && openWeather.so2 !== null ? openWeather.so2 + ' µg/m³' : noData}}
            </div>
          </div>
          <div 
            class="weather-modal__text-data" 
            :title="paramsTitles.airPM25[currentLanguageNumber]"
            :style="{ 'background-color': AirQualityService.getColor('PM2_5', openWeather.pm2_5 ) }"
          >
            <div class="weather-modal__text-data-label">PM 2.5</div>
            <div class="weather-modal__text-data-value">
            {{openWeather && openWeather.pm2_5 !== null
              ? openWeather.pm2_5 + ' µg/m³'
              : noData}}
            </div>
          </div>
          <div 
            class="weather-modal__text-data" 
            :title="paramsTitles.airPM10[currentLanguageNumber]"
            :style="{ 'background-color': AirQualityService.getColor('PM10', openWeather.pm10) }"
          >
            <div class="weather-modal__text-data-label">PM 10</div>
            <div class="weather-modal__text-data-value">
            {{openWeather && openWeather.pm10 !== null
              ? openWeather.pm10 + ' µg/m³'
              : noData}}
            </div>
          </div>
        </div>
      </div>
      <div v-else class="weather-modal__no-data">
        <translated-text-component translation="modal_ow_no_data" />
      </div>
  </div>
</template>

<script lang="ts">
import {
  computed,
  defineComponent, Ref, ref,
} from 'vue';
import { IOpenWeather } from '@/interfaces/IOpenWeather';
import useWeatherDataParser from '@/components/composers/useWeatherDataParser';
import AirQualityService from '@/services/AirQualityService';
import store from '@/store/index';

const TemplateModalOpenWeather: object = defineComponent({
  props: {
    templateData: {
      type: Object,
      required: true,
    },
  },
  name: 'TemplateModalOpenWeather',
  setup(props) {
    const noData: Ref<string> = ref('-');
    const currentLanguageNumber: Ref<number> = computed((): number => store.getters.getCurrentLang);
    const currentWeather: Ref<any> = ref({
      airCO: 0,
      airCO2: 0,
      airNO: null,
      airNO2: 0,
      airSO2: 0,
      airPM25: 0,
      airPM10: 0,
      airHC: 0,
    });
    const paramsTitles = ref({
      airCO: {
        0: 'Emisja tlenku węgla',
        1: 'Carbon monoxide emission',
        2: 'ohlenmonoxid-Emission',
      },
      airNO2: {
        0: 'Emisja dwutlenku azotu',
        1: 'Emission of nitrogen dioxide',
        2: 'Emission von Stickstoffdioxid',
      },
      airSO2: {
        0: 'Emisja dwutlenku siarki',
        1: 'Sulfur dioxide emission',
        2: 'Schwefeldioxid-Emission',
      },
      airPM25: {
        0: 'Pył zawieszony PM2.5',
        1: 'Suspended dust PM2.5',
        2: 'Schwebender Staub PM2.5',
      },
      airPM10: {
        0: 'Pył zawieszony PM10',
        1: 'Suspended dust PM10',
        2: 'Schwebender Staub PM10',
      },
      humidity: {
        0: 'Wilgotność',
        1: 'Humidity',
        2: 'Feuchtigkeit',
      },
      pressure: {
        0: 'Ciśnienie',
        1: 'Pressure',
        2: 'Druck',
      },
      windSpeed: {
        0: 'Prędkość wiatru',
        1: 'Wind speed',
        2: 'Windgeschwindigkeit',
      },
      windDirection: {
        0: 'Kierunek wiatru',
        1: 'Wind direction',
        2: 'Windrichtung',
      },
      tempSurf: {
        0: 'Temperatura nawierzchni',
        1: 'Temperature surface',
        2: 'Temperatur Oberfläche',
      },
      dewPoint: {
        0: 'Temperatura punktu rosy',
        1: 'Dew point temperature',
        2: 'Taupunkttemperatur',
      },
      precipitationOnMinute: {
        0: 'Opady na godzine',
        1: 'Rainfall per hour',
        2: 'Taupunkttemperatur',
      },
      visibility: {
        0: 'Widoczność',
        1: 'Visibility',
        2: 'Sichtweite',
      },
      tempAir: {
        0: 'Temperatura powietrza',
        1: 'Air temperature',
        2: 'Lufttemperatur',
      },
      salt: {
        0: 'Zasolenie',
        1: 'Salinity',
        2: 'Salzgehalt',
      },
    });
    // const descriptionTop = ref('');

    // onMounted(() => {
    //   const { metaData } = props.templateData;
    //   descriptionTop.value = metaData.description;
    // });

    const openWeather: Ref<IOpenWeather> = computed((): IOpenWeather => store.getters.getOpenWeather);

    return {
      openWeather,
      noData,
      currentWeather,
      currentLanguageNumber,
      paramsTitles,
      AirQualityService,
      ...useWeatherDataParser(),
    };
  },
});
export default TemplateModalOpenWeather;
</script>

<style lang="scss">
  .weather-modal {
    height: 100%;
    display: flex;
    flex-direction: column;
    color: white;
    font-size: rem(10px);
    font-weight: 900;
    text-shadow: 0px 3px 6px #00000029;

    &__container{
      padding-top: rem(20px);
      width: 100%;
      margin: 0 auto;
      height: 100%;
      overflow-x: auto;
      display: flex;
      flex-direction: column;
    }

    &__air-quality{
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
      padding-bottom: 0;
    }

    &__current-weather{
      padding-bottom: rem(28px);
    }

    &__weather-details{
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
      text-align: center;
      margin-bottom: 24px;
    }

    &__text-data{
      display: flex;
      flex-direction: column;
      align-items: center;
      width: 25%;
      padding: 0 rem(5px);
      text-align: center;

      .dark-theme & {
        text-shadow: 0 0 3px black;
      }

      &-label{
        font-size: rem(12px);
        font-weight: 700;
      }

      &-value{
        font-size: rem(12px);
      }
    }

    &__no-data {
      font-size: rem(16px);
      text-align: center;
    }

    &__icon-data{
      display: flex;
      flex-direction: column;
      align-items: center;
      width: 25%;
      padding: 0 rem(5px);
      text-align: center;

      &-value{
        font-size: rem(12px);
      }

      &-icon{
        font-size: rem(25px);
        height: rem(25px);
        margin-bottom: rem(12px);

        i{
          display: block;
        }
      }
    }

    &__description {
      text-align: center;
    }

    &__wrapper {
      margin-top: rem(20px);
    }

    &__param {
      &-box {
        margin: 0 rem(14px);
        margin-bottom: rem(13px);
      }

      &-name {
         font-weight: 700;
         font-size: rem(12px);
         margin-bottom: rem(6px);
      }

      &-value {
         font-weight: 900;
         font-size: rem(20px);
      }
    }
  }
</style>
